import { useMemo } from "react";

import {
  discountAmount,
  invoiceTaxSummary,
  TaxCodeLike,
} from "../invoiceTax.utils";
import { DiscountType, InvoiceFormItem } from "./invoice-form.types";

/**
 * Invoice form totals (QA batch 17, BILL-039): each line is taxed with its own
 * tax code (`item.taxCodeId`, else the default code); see invoiceTax.utils.
 */
export const useInvoiceTotals = (params: {
  items: InvoiceFormItem[];
  discount: string;
  discountType: DiscountType;
  isIncludeTax: boolean;
  taxCodes: TaxCodeLike[];
  defaultTaxCodeId?: string | null;
}) => {
  const {
    items,
    discount,
    discountType,
    isIncludeTax,
    taxCodes,
    defaultTaxCodeId,
  } = params;

  return useMemo(() => {
    const subtotal = items.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0,
    );
    const discountValue = discountAmount(
      subtotal,
      Number(String(discount ?? "").replace(/,/g, "")) || 0,
      discountType,
    );
    const summary = invoiceTaxSummary({
      items: items.map((item) => ({
        amount: Number(item.amount) || 0,
        taxCodeId: item.taxCodeId || defaultTaxCodeId || null,
      })),
      taxCodes,
      discountValue,
      isIncludeTax,
    });

    return {
      subtotal,
      discountValue,
      taxableAmount: summary.taxableAmount,
      taxBaseAmount: summary.taxBaseAmount,
      totalTaxValue: summary.totalTax,
      taxRows: summary.rows,
      total: summary.total,
    };
  }, [items, discount, discountType, isIncludeTax, taxCodes, defaultTaxCodeId]);
};
